// Open House — the new Post a Project's book writing steps (minimums.md, Book
// writing and Last screen): Format → Writer tier → Outline (human-written only)
// → Book details → Review & post.
//
// Fields shared with today's form are today's field objects, taken from
// today's config (lib/post/services/bookWriting.js) so labels, options, prices
// and rules stay in one place; FieldRenderer renders them. Only the new flow's
// own answers are defined here: the title-or-topic wording, the short
// description, the outline choice and the open-to-every-level choice. Nothing
// here asks for an author name, managed services, QA or QC.
//
// The pieces every Open House service shares (the title-or-topic and short
// description fields, the tier field, the rail, validation, issue targeting
// and the review) live here too; sampleSteps.js and editingSteps.js reuse them.

import { bookWriting, writerRateFor } from "@/lib/post/services/bookWriting"
import { addonPriceFields } from "@/lib/post/addon"
import { ALL_TIER_VALUE, AI_CONSENT_LABELS } from "@/lib/post/options"
import { fieldError, fieldOk, isEmpty, resolveOptions, visibleFieldsOf } from "@/lib/post/fieldValidation"
import { WORDING } from "./wording"

// The cursor stage stored in current_step_api for an Open House draft.
export const STAGE = "open_house"

// Today's design defaults, plus Premium preselected (decision 17).
export const DEFAULTS = { ...bookWriting.defaults, tier: "Premium" }

// Today's macro-stage label for the last step (PostProject MACRO_STEP_LABELS).
const REVIEW_NODE = "Review & post"
export const REVIEW_STEP = {
  id: "review", node: REVIEW_NODE, title: WORDING.reviewHeading, sub: WORDING.reviewSub, fields: [],
}
// Today's rail footer (PostProject buildRailProps).
const RAIL_NOTE = "Delivery estimate shared once a freelancer is attached."

const OUTLINE_EXTENSIONS = [".doc", ".docx", ".pdf", ".zip"]

export const money = (n) =>
  `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// The largest outline a guest may upload (OpenHouse::GuestDraft::MAX_OUTLINE_BYTES).
export const OUTLINE_MAX_BYTES = 24 * 1024 * 1024

export const isOutlineFile = (file) =>
  OUTLINE_EXTENSIONS.some((ext) => String(file?.name || "").toLowerCase().endsWith(ext))

const byId = (steps) => Object.fromEntries(steps.map((step) => [step.id, step]))
const field = (step, key) => step.fields.find((f) => f.key === key || f.type === key)

// The approved title-or-topic field, and a short description in a service's wording.
export const TITLE_FIELD = {
  type: "text", key: "title", required: true, label: WORDING.titleLabel, help: WORDING.titleHelp,
  placeholder: WORDING.titlePlaceholder, errorText: WORDING.titleError,
}

export const descriptionField = (help, placeholder, errorText) => ({
  type: "textarea", key: "short_description", required: true, rows: 4, label: WORDING.descriptionLabel,
  help, placeholder, errorText,
})

// Today's tier field for these answers, without All Tier Access, Premium tagged.
export function tierField(base) {
  return {
    ...base,
    options: (v, ctx) =>
      base.options(v, ctx)
        .filter((o) => o.value !== ALL_TIER_VALUE)
        .map((o) => (o.value === "Premium" ? { ...o, tag: WORDING.recommended } : o)),
  }
}

export function openHouseSteps(values = {}, ctx = {}) {
  const today = byId(bookWriting.steps(values, ctx))
  const details = today.details.fields

  const steps = [
    {
      id: "format",
      node: today.format.node,
      title: today.format.title,
      sub: today.format.sub,
      fields: [
        field(today.format, "fiction"),
        { ...field(today.manuscript, "use_ai"), label: today.manuscript.title, when: today.manuscript.when },
        { ...field(today.consent, "consent"), when: (v) => v.fiction != null && !v.use_ai },
      ],
    },
    {
      id: "tier",
      node: today.tier.node,
      title: today.tier.title,
      sub: today.tier.sub,
      fields: [
        tierField(field(today.tier, "tier")),
        { type: "openLevels", key: "open_levels", price: openLevelsPrice },
        ...details.filter((f) => f.type === "wordcount"),
        { ...field(today.editing, "editing_style"), label: today.editing.title },
        field(today.details, "images"),
      ],
    },
    {
      id: "outline",
      node: today.outline.node,
      title: WORDING.outlineHeading,
      sub: WORDING.outlineIntro,
      when: (v) => !v.use_ai,
      fields: [{ type: "outline", key: "outline_choice" }],
    },
    {
      id: "details",
      node: today.details.node,
      title: today.details.title,
      fields: [
        TITLE_FIELD,
        descriptionField(WORDING.descriptionHelp, WORDING.descriptionPlaceholder, WORDING.descriptionError),
        field(today.details, "genre"),
        field(today.details, "writing_style"),
        field(today.details, "niche"),
        field(today.details, "nsfw"),
        field(today.details, "triggerWarning"),
      ],
    },
    REVIEW_STEP,
  ]

  return steps.filter((step) => !step.when || step.when(values, ctx))
}

// ── outline ──────────────────────────────────────────────────────────────────

// Today's paid outline option (label + live price) for these answers.
export function paidOutline(values, ctx) {
  const today = byId(bookWriting.steps(values, ctx))
  return field(today.outline, "add_outline").options(values, ctx).find((o) => o.value === true)
}

export function outlineChoice(values, oh) {
  if (values.add_outline) return "paid"
  return oh.supplies_outline ? "own" : null
}

// Only the chosen source (outline_source) counts: the saved file for "upload"
// (a file still uploading is not supplied yet), the pasted text for "paste".
export function outlineSupplied(oh) {
  if (oh.outline_source === "upload") return !oh.pendingFile && !!oh.outlineFile
  if (oh.outline_source === "paste") return !isEmpty(oh.outline_text)
  return false
}

// ── validation ───────────────────────────────────────────────────────────────

// The message for one unmet field, or null. `values` carries the short
// description beside the posting's own answers; `oh` the Open House answers.
// A field may carry its own check (`issue`), as editing's manuscript does.
export function fieldIssue(f, values, ctx, oh) {
  if (f.when && !f.when(values, ctx)) return null
  if (f.issue) return f.issue(values)
  if (f.type === "openLevels") return null
  if (f.type === "outline") {
    const choice = outlineChoice(values, oh)
    if (!choice) return WORDING.choiceError
    if (choice !== "own" || outlineSupplied(oh)) return null
    return oh.outline_source === "upload" && oh.pendingFile ? WORDING.uploading : WORDING.requiredError
  }
  if (f.key === "tier" && values.tier === ALL_TIER_VALUE) return null
  if (f.errorText) return fieldOk(f, values, ctx) ? null : f.errorText

  return fieldError(f, values, ctx)
}

export function stepIssues(step, values, ctx, oh) {
  return visibleFieldsOf(step, values, ctx)
    .map((f) => [f.key || f.type, fieldIssue(f, values, ctx, oh)])
    .filter(([, message]) => message)
}

// Server issue fields that live under another field's control.
export const SENSITIVE_ALIASES = {
  nsfw_details: "nsfw", trigger_warning: "triggerWarning", trigger_warning_details: "triggerWarning",
}
export const ISSUE_ALIASES = {
  add_outline: "outline_choice", outline_text: "outline_choice", outline_file: "outline_choice",
  file: "outline_choice", package_type: "outline_choice", ...SENSITIVE_ALIASES,
}

// The step and field a submit issue ({ service, field, message }) belongs to.
export function issueTarget(issue, steps, values, ctx, aliases = ISSUE_ALIASES) {
  const raw = String(issue?.field || "")
  const key = raw.startsWith("ai_consent") ? "ai_consent" : aliases[raw] || raw
  for (const [index, step] of steps.entries()) {
    if (visibleFieldsOf(step, values, ctx).some((f) => (f.key || f.type) === key)) return { index, key }
  }
  const outline = steps.findIndex((step) => step.id === "outline")
  return issue?.service === "outline" && outline >= 0 ? { index: outline, key: "outline_choice" } : null
}

// ── price ────────────────────────────────────────────────────────────────────

// The open-to-every-level range for a tier field: the lowest eligible level to Premium.
export function levelRange(tier, values, ctx) {
  const levels = tier.options(values, ctx)
  return [levels[levels.length - 1]?.value, "Premium"]
}

export const writingLevels = (values, ctx) =>
  levelRange(tierField(field(byId(bookWriting.steps(values, ctx)).tier, "tier")), values, ctx)

// The open-to-every-level card's price line, per 100 words.
export function openLevelsPrice(values, ctx) {
  const [low, high] = writingLevels(values, ctx).map((tier) => writerRateFor(tier, !!values.use_ai, ctx))
  return low != null && high != null ? WORDING.openLevelsPrice(money(low), money(high)) : null
}

// Rail lines and total from a service's own estimate (today's config). One
// level posts its exact price through today's /post/price payload; open to
// every level (`levels`, its lowest and highest) shows the range from the lowest.
export function railWith(config, values, ctx, levels = []) {
  const estimate = config.estimate(values, ctx)
  if (values.tier !== ALL_TIER_VALUE) {
    const payload = config.pricePayload(values)
    return { ...estimate, isRange: false, note: RAIL_NOTE, payload: payload && { ...payload, ...addonPriceFields() } }
  }

  const [low, high] = levels.map((tier) => config.estimate({ ...values, tier }, ctx))
  const ranged = low?.lines.length > 0 && high?.lines.length > 0
  return {
    ...estimate,
    total: ranged ? low.total : estimate.total,
    isRange: true,
    fromLabel: "From",
    note: ranged ? WORDING.openLevelsTotal : undefined,
    payload: null,
  }
}

export const railFor = (values, ctx) => railWith(bookWriting, values, ctx, writingLevels(values, ctx))

// ── review ───────────────────────────────────────────────────────────────────

function reviewValue(f, values, ctx, oh) {
  if (f.reviewValue) return f.reviewValue(values, ctx)
  const raw = values[f.key]
  switch (f.type) {
    case "outline": {
      const choice = outlineChoice(values, oh)
      if (choice === "paid") {
        const paid = paidOutline(values, ctx)
        // The guest page shows only the quote's fee (ctx.quotedOutline).
        if (ctx?.guest) return ctx.quotedOutline ? `${paid.label} · ${ctx.quotedOutline}` : paid.label
        return `${paid.label} · ${paid.price}`
      }
      if (choice !== "own") return null
      if (!outlineSupplied(oh)) return null
      return oh.outline_source === "upload" ? oh.outlineFile.filename : WORDING.pasteLabel
    }
    case "radio": {
      if (f.key === "tier" && raw === ALL_TIER_VALUE) return WORDING.openLevels
      if (raw == null) return null
      return resolveOptions(f, values, ctx).find((o) => String(o.value) === String(raw))?.label ?? null
    }
    case "consent":
      return AI_CONSENT_LABELS[values[f.key]] || null
    case "wordcount":
      return Number(raw) > 0 ? `${Number(raw).toLocaleString()} words` : null
    case "toggle":
      if (!raw) return null
      return f.detailsKey && values[f.detailsKey] ? `Yes · ${values[f.detailsKey]}` : "Yes"
    case "triggerWarning": {
      if (!values.trigger_warning) return null
      const parts = [...(values.trigger_warning_warnings || [])]
      if (!isEmpty(values.trigger_warning_other)) parts.push(values.trigger_warning_other)
      return parts.length ? parts.join(", ") : "Yes"
    }
    case "openLevels":
      return null
    default:
      return isEmpty(raw) ? null : String(raw)
  }
}

// One section per step before Review, each row an answered visible field.
export function reviewSections(steps, values, ctx, oh) {
  return steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => step.id !== "review")
    .map(({ step, index }) => ({
      id: step.id,
      title: step.node,
      index,
      rows: visibleFieldsOf(step, values, ctx)
        .map((f) => ({ label: f.reviewLabel || f.label || WORDING.outlineHeading, value: reviewValue(f, values, ctx, oh) }))
        .filter((row) => row.value != null && row.value !== ""),
    }))
}
